import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ForgotPassword.scss";
import { LanguageSelect } from "../../pages/LanguageSelect";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, Input, InputLabel, Link } from "@material-ui/core";
import {
  useNetworkStatus,
  useTranslations,
} from "examsbook-react-sdk/custom-hooks";
import i18n from "examsbook-react-sdk/translations";
import { forgotPassword } from "examsbook-react-sdk/actions/getUser";
import LearnifyIcon from "../../assets/images/LearnifyLogoDark.png";
import { ColorsConstant } from "../../constants/colorConstant";
import {
  getSettingsAction,
  setSubdomain,
} from "examsbook-react-sdk/actions/getSettingsAction";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

const ForgotPassword = () => {
  const changePasswordBtnRef = useRef(null);
  const history = useHistory();
  const [bgColor, setBgColor] = useState(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [textColor, setTextColor] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dynamicLogo, setDynamicLogo] = useState(null);
  const [settingsData, setSettingsData] = useState([]);
  const isOnline = useNetworkStatus();
  const dispatch = useDispatch();
  const t = useTranslations();
  const [subdomainName, setSubDomainName] = useState("");
  const language =
    useSelector((state) => state.getLanguaggeReducer.lang) || "en";

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);
  useEffect(() => {
    getSubdomain();
  }, []);

  useEffect(() => {
    const dynamicLogos =
      settingsData?.data?.[0]?.branding?.logos?.webAndAppLogo?.imagePath;
    setDynamicLogo(dynamicLogos ? dynamicLogos : LearnifyIcon);
    setBgColor(
      settingsData?.data?.[0]
        ? settingsData?.data?.[0]?.branding?.backgroundAndTextTheme
          ?.primaryBackgroundColor
        : ColorsConstant.defaultBlue
    );
    setTextColor(
      settingsData?.data?.[0]
        ? settingsData?.data?.[0]?.branding?.backgroundAndTextTheme
          ?.primaryTextColor
        : ColorsConstant.white
    );
  }, [isLoading, settingsData]);

  const focusTextField = (fieldId) => {
    // check if we want it.
    const field = document.getElementById(fieldId);
    if (field) {
      field.focus();
    }
  };

  const handleEmailChange = (event) => {
    const value = event.target.value.trim();
    setEmail(value);
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isMobileNumber = (str) => /^\d{10}$/.test(str);
    let errMsg = "";
    let emailValid = true;
    // Validate email or mobile number
    if (!isValidEmail(value) && !isMobileNumber(value)) {
      errMsg = t("Please enter a valid email id or 10-digit mobile number.");
      emailValid = false;
    }
    // Handle error message and button state
    const errorElement = document.getElementById("reset-password-error");
    if (!emailValid) {
      errorElement.innerHTML = `<div class="errorMsg">${errMsg}</div>`;
      errorElement.style.display = "block";
      if (changePasswordBtnRef.current) {
        changePasswordBtnRef.current.setAttribute("disabled", "disabled");
      }
      setIsButtonDisabled(true);
      focusTextField("userForgotPassEmail");
    } else {
      errorElement.style.display = "none";
      if (changePasswordBtnRef.current) {
        changePasswordBtnRef.current.removeAttribute("disabled");
      }
      setIsButtonDisabled(false);
    }
  };

  const getSubdomain = () => {
    const host = window.location.host;
    const domainParts = host.split(".");
    if (process.env.REACT_APP_TENANT_DOMAIN) {
      setSubDomainName(process.env.REACT_APP_TENANT_DOMAIN);
      dispatch(setSubdomain(process.env.REACT_APP_TENANT_DOMAIN));
    } else if (domainParts.length > 3) {
      const domainName = domainParts[0];
      setSubDomainName(domainName);
      dispatch(setSubdomain(domainName));
    }
  };

  const getSettingsApiData = useCallback(async () => {
    try {
      await dispatch(getSettingsAction(subdomainName)).then((res) => {
        setSettingsData(res?.data);
        setIsLoading(true);
      });
    } catch (error) {
      console.log("Error -", error.message);
    }
  }, [dispatch, subdomainName]);

  useEffect(() => {
    if (subdomainName && subdomainName !== null) {
      getSettingsApiData();
    }
  }, [subdomainName, getSettingsApiData]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    let forgotPasswordData = {
      [isNaN(+email) ? "email" : "mobileNumber"]: email?.toLowerCase(),
      subdomain: subdomainName,
      userLogin: true,
    };

    const isValidEmail = (email) =>
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email.toLowerCase());
    const isMobileNumber = (str) => /^\d{10}$/.test(str);
    const errorElement = document.getElementById("reset-password-error");
    const inputType = isValidEmail(email)
      ? "email"
      : isMobileNumber(email)
        ? "mobileNumber"
        : null;
    if (!inputType) {
      errorElement.innerHTML = `<div class="errorMsg">${t("Please enter a valid email id or 10-digit mobile number.")}</div>`;
      errorElement.style.display = "block";
      return;
    }

    errorElement.style.display = "none";
    dispatch(forgotPassword(forgotPasswordData)).then((res) => {
      if (res.status === 200) {
        history.push("/verify-otp", { userIdentifier: email });
        errorElement.innerHTML = `<div class=" messageBox successMsg">${t(res.message)}</div>`;
        errorElement.style.display = "block";
        if (changePasswordBtnRef.current) {
          changePasswordBtnRef.current
            .getElementById("changePasswordBtn")
            .setAttribute("disabled", "disabled");
        }
      } else {
        errorElement.innerHTML = `<div class=" messageBox errorMsg">${t(res.message)}</div>`;
        errorElement.style.display = "block";
      }
    });
  };

  return (
    <Grid className="forgot-password-parent-div">
      <LanguageSelect />
      <Grid className="forgot-password-child-div">
        {isLoading && dynamicLogo && (
          <Box
            component="img"
            id="login-img-learn"
            className="forgot-password-logo"
            src={dynamicLogo}
            alt="Learnify"
          />
        )}
      </Grid>
      {!isOnline && (
        <Box className="form-group error-msg" style={{ display: "block" }}>
          {t("No internet connection!")}
        </Box>
      )}
      <Grid className="forgot-password-right-side-box">
        <Box className="form-group error-msg" id="reset-password-error"></Box>
        <Box className="mdl-card__supporting-text input-fields">
          <Box className="forget-title" style={{ color: `${bgColor}` }}>
            {t("Forgot Password")}
          </Box>
          <Box>
            <Box component="p" className="custom-sub-title">
              {t(
                "Enter your email address or mobile number, and we’ll send you a verification code to reset your password."
              )}
            </Box>
          </Box>
          <Box
            component="form"
            id="userForgotPassForm"
            className="form-horizontal"
            onSubmit={handleForgotPassword}
          >
            <Box
              className="mdc-text-field mdc-text-field--outlined"
              id="forgot-input"
            >
              <Input
                type="text"
                className="input-box required_text"
                id="userForgotPassEmail"
                placeholder={t("Email/Mobile No.")}
                onChange={handleEmailChange}
              />
              <Box>
                <InputLabel
                  className="mdc-text-field__label"
                  style={{ color: `${bgColor}`, padding: "2px" }}
                  htmlFor="userForgotPassEmail"
                >
                  {t("Email/Mobile No.")}
                </InputLabel>
              </Box>
            </Box>
            <Box className="otg otg-items-1 text-center verify-proceed-button send-reset-link-div">
              <Button
                ref={changePasswordBtnRef}
                type="submit"
                id="changePasswordBtn"
                className={`send-reset-link-button ${isButtonDisabled ? "disabled-btn" : ""
                  }`}
                style={{ backgroundColor: `${bgColor}`, color: `${textColor}` }}
                disabled={isButtonDisabled}
              >
                {t("Send Verification Code")}
              </Button>
            </Box>
            <Box className="back-login">
              <Link
                component={RouterLink}
                className="back-login"
                to="/login"
                style={{ color: `${bgColor}` }}
              >
                {t("Back to Login")}
              </Link>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;